// The date window, for every screen that has one.
//
// Promoted here because there were already TWO implementations — `Payments.shared.js`
// (`RANGE_PRESETS`/`presetRange`, ids `today|week|month|all`) and
// `Operations.shared.js` (`DATE_PRESETS`/`presetRange`, keys `today|7d|month|all`) —
// which disagreed about what «این هفته» means and computed the Jalali month start
// two different ways. A third copy for the reports module would have made the same
// filter name three different windows in one app, and both older screens now import
// from here so there is one answer.
//
// Everything is computed on the **clinic's wall clock** (`clinicZone.js`, PM-0088)
// on the **Jalali calendar**. Both halves matter: «این ماه» is a Jalali month
// beginning at the CLINIC's midnight — Tehran's for a Tehran clinic, Baghdad's for a
// Baghdad one — and a `new Date().setHours(0,0,0,0)` opens the day at the BROWSER's
// midnight, so takings near midnight fall out of «امروز» or leak into it.
//
// A BRAND on the Gregorian calendar (`calendar: 'gregory'`, docs/rebranding/
// CONTRACT.md) keeps Gregorian books: there «این ماه» / "this month" is the
// Gregorian month, «امسال» the Gregorian year, and a folded bucket a Gregorian
// month — the same clinic clock, the other calendar. Without one, nothing below
// moves: `calendarOf`/`calendarStartMs` ARE the Jalali conversions they replaced.

import { calendarSystem } from '@parsina/framework/ui/lib/brand';
import { jalaliToMs, msToJalali } from './jalali';
import {
  addDaysIso, clinicDayEnd, clinicDayStart, isoDayOf, wallToMs, zoneOffsetMs,
} from './clinicZone';

export const DAY_MS = 86_400_000;

// ── Day boundaries on the clinic's clock ─────────────────────────────────────

/** The clinic's midnight of the day an instant falls in. */
export function startOfDay(ts) {
  return clinicDayStart(ts);
}

/** The last millisecond of that day — windows are INCLUSIVE at both ends. A DST day
 * is 23 or 25 hours long, so this is the next midnight, not start + 24h. */
export const endOfDay = (ts) => clinicDayEnd(ts);

const pad2 = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' (what `JalaliDatePicker` emits) → that clinic day's first/last ms. */
export function gStrStart(s) {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return NaN;
  return wallToMs(`${y}-${pad2(m)}-${pad2(d)}`, 0);
}

export function gStrEnd(s) {
  const start = gStrStart(s);
  return Number.isNaN(start) ? NaN : endOfDay(start);
}

/** Epoch ms → 'YYYY-MM-DD' Gregorian as read on the clinic's clock (the picker's format). */
export function toGStr(ts) {
  return isoDayOf(ts);
}

// ── Presets ──────────────────────────────────────────────────────────────────

/**
 * The seven windows every time-based report offers.
 *
 * `lastMonth`, `3m` and `year` are new here; the two older screens had only the
 * first three plus «همه». «همه» is deliberately NOT in this list: an unbounded
 * report asks a clinic's whole history for one screen, and the BFF refuses a window
 * longer than a year anyway. The screens that still offer it keep their own entry.
 */
export const RANGE_PRESETS = (t) => ([
  { id: 'today', label: t('common.dates.today') },
  { id: 'week', label: t('reports.range.week') },
  { id: 'month', label: t('reports.range.month') },
  { id: 'lastMonth', label: t('reports.range.lastMonth') },
  { id: '3m', label: t('reports.range.threeMonths') },
  { id: 'year', label: t('reports.range.year') },
  { id: 'custom', label: t('reports.range.custom') },
]);

export const PRESET_IDS = ['today', 'week', 'month', 'lastMonth', '3m', 'year', 'custom'];

/**
 * The Iranian week starts on SATURDAY.
 *
 * `getUTCDay()` is 0=Sunday…6=Saturday, so the step back to the week's start is
 * `(day + 1) % 7`. A named function rather than an inline expression because getting
 * it wrong shifts «این هفته» by a day and silently changes every weekly figure on
 * every screen that imports this file.
 */
export function startOfWeek(ts) {
  const iso = isoDayOf(ts);
  const dow = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return wallToMs(addDaysIso(iso, -((dow + 1) % 7)), 0);
}

/**
 * The Jalali calendar, read on the CLINIC's clock (Tehran's until PM-0088).
 *
 * `msToJalali`/`jalaliToMs` in the kit work off a UTC anchor: they read an instant
 * as the UTC calendar day it falls in, and return UTC midnight of the day they are
 * given. That is the kit's documented convention — `iranNow()` is `Date.now() +
 * IRAN_OFFSET_MS` read with `getUTC*` — and using the pair raw here shifted every
 * Jalali boundary in this file by three and a half hours: «این ماه» opened at 03:30
 * on the first, «ماه قبل» ended at 03:30 on the first of THIS month (counting the
 * small hours of the wrong month), and `foldBuckets` — which converts a Tehran
 * midnight — read every day as the one before it, so the first day of a Jalali month
 * folded into the previous month's bucket.
 *
 * Shifting on the way in and back out on the way out is the whole correction, and it
 * belongs here rather than in each call site: there were eleven of them.
 */
const jalaliOf = (ts) => msToJalali(ts + zoneOffsetMs(ts));
const jalaliStartMs = (jy, jm, jd) => wallToMs(new Date(jalaliToMs(jy, jm, jd)).toISOString().slice(0, 10), 0);

/** The clinic's calendar: Jalali, unless the brand puts the panel on the Gregorian one. */
export const gregorianBooks = () => calendarSystem('jalali') === 'gregory';

/** An instant → `{ y, m, d }` on the clinic's clock, in the clinic's calendar. */
function calendarOf(ts) {
  if (!gregorianBooks()) return jalaliOf(ts);
  const [y, m, d] = isoDayOf(ts).split('-').map(Number);
  return { y, m, d };
}

/** The clinic's midnight of a date in the clinic's calendar. */
function calendarStartMs(y, m, d) {
  return gregorianBooks() ? wallToMs(`${y}-${pad2(m)}-${pad2(d)}`, 0) : jalaliStartMs(y, m, d);
}

/**
 * The clinic's midnight on the first day of `now`'s month, in the clinic's
 * calendar — for a screen with its own «این ماه» that has to read the Gregorian
 * month under a brand on that calendar.
 */
export function monthStartMs(now = Date.now()) {
  const j = calendarOf(now);
  return calendarStartMs(j.y, j.m, 1);
}

/** Twelve months a year in both calendars, so month arithmetic is the same. */
function addMonths(y, m, delta) {
  const total = y * 12 + (m - 1) + delta;
  return { y: Math.floor(total / 12), m: (total % 12) + 1 };
}

/**
 * A preset id → its inclusive `{ fromTs, toTs }` window.
 *
 * The open-ended presets end at NOW rather than at end-of-day: a report that ran to
 * midnight tonight would carry a few hours of guaranteed emptiness at the right edge
 * of every chart, and «امروز» would claim a day that has not happened yet.
 */
export function presetRange(id, now = Date.now()) {
  const j = calendarOf(now);
  switch (id) {
    case 'today':
      return { fromTs: startOfDay(now), toTs: now };
    case 'week':
      return { fromTs: startOfWeek(now), toTs: now };
    case 'lastMonth': {
      const prev = addMonths(j.y, j.m, -1);
      return { fromTs: calendarStartMs(prev.y, prev.m, 1), toTs: calendarStartMs(j.y, j.m, 1) - 1 };
    }
    case '3m': {
      const back = addMonths(j.y, j.m, -2);
      return { fromTs: calendarStartMs(back.y, back.m, 1), toTs: now };
    }
    case 'year':
      return { fromTs: calendarStartMs(j.y, 1, 1), toTs: now };
    case 'month':
    default:
      return { fromTs: calendarStartMs(j.y, j.m, 1), toTs: now };
  }
}

/**
 * URL parameters → the window to ask for.
 *
 * Two guards, and both exist because a URL is hand-editable and a bookmark outlives
 * the state that made it:
 *
 *   • an incomplete custom range falls back to «این ماه» rather than to all-time, so
 *     a half-typed URL cannot ask for every invoice the clinic has ever raised;
 *   • a reversed window is swapped rather than honoured, because it matches nothing
 *     at all — which renders as a clinic that did no business, not as an error.
 */
export function resolveRange(preset, from, to, now = Date.now()) {
  if (preset === 'custom') {
    const a = gStrStart(from);
    const b = gStrEnd(to);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return presetRange('month', now);
    return a <= b ? { fromTs: a, toTs: b } : { fromTs: gStrStart(to), toTs: gStrEnd(from) };
  }
  return presetRange(PRESET_IDS.includes(preset) ? preset : 'month', now);
}

// ── Bucketing ────────────────────────────────────────────────────────────────

/**
 * How wide a bar should be, for a window of this length.
 *
 * Chosen so a chart never draws more than about fifty bars: past that the axis
 * labels collide, the bars are a pixel wide, and the shape stops being readable —
 * which is the only thing a trend chart is for.
 */
export function bucketFor({ fromTs, toTs }) {
  const days = Math.ceil((toTs - fromTs) / DAY_MS);
  if (days <= 45) return 'day';
  if (days <= 130) return 'week';
  return 'month';
}

/**
 * Fold the server's per-day rows into Jalali weeks or months.
 *
 * The services bucket by **Gregorian day in Tehran** and hold no Jalali opinion at
 * all — deliberately, so that the calendar lives in exactly one layer. This is that
 * layer. A Jalali month is not a Gregorian one shifted by a constant, so folding
 * cannot be done by arithmetic on the key; each day is converted and re-keyed.
 * Under a brand on the Gregorian calendar the weeks and months are Gregorian ones;
 * `_jalali` then carries the Gregorian parts (the field keeps its name — every
 * reader only labels the bucket with its numbers).
 *
 * `sum` names the numeric fields to add. Anything not named is taken from the FIRST
 * day of the bucket, which is what a label wants and what a running total does not —
 * so pass every numeric field you intend to see.
 */
export function foldBuckets(rows, bucket, sum = []) {
  if (!rows?.length || bucket === 'day') return rows || [];
  const out = new Map();
  for (const row of rows) {
    const ts = gStrStart(row.day || row.key);
    if (!Number.isFinite(ts)) continue;
    const j = calendarOf(ts);
    const key = bucket === 'month'
      ? `${j.y}-${String(j.m).padStart(2, '0')}`
      : `${j.y}-${String(j.m).padStart(2, '0')}-w${Math.ceil(j.d / 7)}`;
    const slot = out.get(key);
    if (!slot) {
      out.set(key, { ...row, day: key, _from: ts, _jalali: j, _bucket: bucket });
    } else {
      for (const field of sum) slot[field] = (slot[field] || 0) + (row[field] || 0);
    }
  }
  return [...out.values()];
}

/**
 * The comparison window, for a caption.
 *
 * The BFF computes the authoritative one and returns it on every report, so this is
 * only for the rare screen that needs to name the period before its data has
 * arrived. Keep the two in step: the rule is "the same elapsed span", so that on the
 * 10th of the month «این ماه» is compared with the first ten days of last month and
 * not with all thirty-one.
 */
export function comparisonRange(preset, window, now = Date.now()) {
  const span = window.toTs - window.fromTs;
  const j = calendarOf(now);
  switch (preset) {
    case 'today': {
      const start = wallToMs(addDaysIso(isoDayOf(now), -1), 0);
      return { fromTs: start, toTs: start + span };
    }
    case 'week': {
      const start = wallToMs(addDaysIso(isoDayOf(startOfWeek(now)), -7), 0);
      return { fromTs: start, toTs: start + span };
    }
    case 'month': {
      const prev = addMonths(j.y, j.m, -1);
      const start = calendarStartMs(prev.y, prev.m, 1);
      return { fromTs: start, toTs: start + span };
    }
    case 'lastMonth': {
      const prev = addMonths(j.y, j.m, -2);
      const next = addMonths(prev.y, prev.m, 1);
      return { fromTs: calendarStartMs(prev.y, prev.m, 1), toTs: calendarStartMs(next.y, next.m, 1) - 1 };
    }
    case '3m': {
      const back = addMonths(j.y, j.m, -5);
      const start = calendarStartMs(back.y, back.m, 1);
      return { fromTs: start, toTs: start + span };
    }
    case 'year': {
      const start = calendarStartMs(j.y - 1, 1, 1);
      return { fromTs: start, toTs: start + span };
    }
    default:
      return { fromTs: window.fromTs - span - 1, toTs: window.fromTs - 1 };
  }
}
